import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
  useCallback,
  useRef,
  useState,
} from 'react';

import { Dropdown } from './dropdown/dropdown';
import { Header3Item as HeaderItem } from './item/item';
import { ItemsAlignment, ItemsDisplayMode } from './config';
import { HeaderContextProvider } from './context';
import styles from './header3.scss';
import { useOnClickOutside, useOverflowItems } from './hooks';
import type { ItemsAlignmentEnumType, ItemsDisplayModeEnumType } from './types';

type HeaderProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    left?: ReactNode;
    right?: ReactNode;
    dataTestId?: string;
    moreButtonText?: string;
    moreButtonIcon?: string;
    itemsAlignment?: ItemsAlignmentEnumType;
    itemsDisplayMode?: ItemsDisplayModeEnumType;
  };

const COMPONENT_ID = 'modulor-header3';

export const Header3: ForwardRefExoticComponent<HeaderProps> = forwardRef<
  HTMLDivElement,
  HeaderProps
>(
  (
    {
      style,
      left,
      right,
      dataTestId = COMPONENT_ID,
      children,
      className,
      moreButtonText = 'More',
      moreButtonIcon = 'more_horizontal',
      itemsAlignment = ItemsAlignment.Center,
      itemsDisplayMode = ItemsDisplayMode.IconsTitles,
      ...rest
    },
    ref,
  ) => {
    const [dropdownIsActive, setDropdownIsActive] = useState<boolean>(false);

    const centerSectionRef = useRef(null);
    const centerSectionItemsRef = useRef(null);
    const moreButtonContainerRef = useRef(null);
    const moreButtonRef = useRef(null);

    const closeMoreDropdown = useCallback(() => {
      setDropdownIsActive(false);
    }, []);

    useOnClickOutside(moreButtonContainerRef, closeMoreDropdown);

    const { visibleItems, hiddenItems, showMoreButton } = useOverflowItems({
      sectionRef: centerSectionRef,
      sectionItemsRef: centerSectionItemsRef,
      moreButtonRef,
      children,
    });

    return (
      <div
        className={classNames(className, styles.container)}
        ref={ref}
        {...rest}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}>
        <div style={style} className={styles.header}>
          {left && (
            <div className={styles.left} data-testid={`${dataTestId}-left`}>
              {left}
            </div>
          )}

          <HeaderContextProvider
            itemsDisplayMode={itemsDisplayMode}
            closeMoreDropdown={closeMoreDropdown}>
            <div
              ref={centerSectionRef}
              className={classNames(styles.center, styles[`alignment--${itemsAlignment}`])}>
              <div
                className={classNames(styles.items)}
                data-testid={`${dataTestId}-items`}
                ref={centerSectionItemsRef}>
                {visibleItems}
              </div>

              <div ref={moreButtonContainerRef} style={{ position: 'relative' }}>
                <div ref={moreButtonRef} style={{ display: showMoreButton ? 'flex' : 'none' }}>
                  <HeaderItem
                    onClick={() => setDropdownIsActive(!dropdownIsActive)}
                    icon={{ name: moreButtonIcon }}
                    label={{ text: moreButtonText }}
                  />
                </div>

                {dropdownIsActive && <Dropdown>{hiddenItems}</Dropdown>}
              </div>
            </div>
          </HeaderContextProvider>

          {right && (
            <div className={styles.right} data-testid={`${dataTestId}-right`}>
              {right}
            </div>
          )}
        </div>
      </div>
    );
  },
);

Header3.displayName = 'Header3';
